import * as React from 'react';
import clsx from 'clsx';
import moment from 'moment';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import { ActivityAction } from '@pec/aion-ui-core/interfaces/activities';
import { Box, Button, Paper, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { formats } from '@pec/aion-ui-i18next/constants';
import { FOUNDATION_EXPIRATION_DATE, LOGOS } from '../../helpers/newDashboard';
import { IMainSubscription } from '@pec/aion-ui-core/interfaces/mainSubscription';
import { InfoDialog } from '../InfoDialog';
import { IOrganizationInfo } from '@pec/aion-ui-core/interfaces/organizationInfo';
import { ISubscription } from '../../interfaces/subscriptions';
import { Link, NavLink, useHistory, useParams } from 'react-router-dom';
import { localizeDate } from '@pec/aion-ui-i18next/helpers/localize';
import { OperationType } from '../../types/enums';
import { refetchSubscriptions, requestRefund } from '../../slices/subscriptionsV2';
import { SubscriptionTiers } from '@pec/aion-ui-core/enums/Subscription';
import { SubscriptionTypePublicId } from '@pec/aion-ui-core/enums/Subscription';
import { getRenewalAvailableInDays } from '@pec/aion-ui-core/constants/subscription';
import { Trans, useTranslation } from 'react-i18next';
import { updateLead } from '../../helpers/events';
import { useAppDispatch } from '../../combineReducers';
import { useFeature } from 'flagged';
import { usePermission } from '../../hooks/usePermission';
import { PaymentStatus } from '@pec/aion-ui-core/interfaces/subscription';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    wrapper: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      minHeight: 200,
      minWidth: 450,
      padding: theme.spacing(3),
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2)
    },
    logo: {
      width: 56,
      height: 56
    },
    noLogo: {
      backgroundColor: '#D9D9D9'
    },
    title: {
      fontSize: 20,
      lineHeight: '24px',
      fontWeight: theme.typography.fontWeightMedium,
      letterSpacing: 0.15,
      color: 'var(--text-black)',
      marginBottom: theme.spacing(0.5),
      paddingTop: theme.spacing(0.5)
    },
    text: {
      fontSize: 14,
      lineHeight: '20px',
      letterSpacing: 0.25,
      color: 'var(--text-black)',
      marginBottom: theme.spacing(2.5)
    },
    textError: {
      color: 'var(--status-error)',
      marginLeft: theme.spacing(1)
    },
    button: {
      marginRight: theme.spacing(1.75)
    },
    navLink: {
      color: 'var(--primary-blue-six)',
      fontWeight: theme.typography.fontWeightMedium,
      textDecoration: 'none'
    },
    scopeChangeLink: {
      color: 'var(--primary-blue-six)',
      marginLeft: theme.spacing(1)
    }
  })
);

export const TestLocators = {
  logo: 'logo',
  description: 'description',
  expirationDate: 'expirationDate',
  upgradeBtn: 'upgradeBtn',
  renewBtn: 'renewBtn',
  refundBtn: 'refundBtn'
};

const operationSessionKeys = ['survey:formState_organizationId_', 'survey:orderPageSelectedProduct_organizationId_'];

type RouteParams = {
  organizationId: string;
};

type Props = {
  subscription: ISubscription;
  mainSubscription: IMainSubscription;
  organizationInfo: IOrganizationInfo | null;
  assessmentType: string | null;
};

export const SubscriptionCard: React.FC<Props> = ({
  subscription,
  mainSubscription,
  organizationInfo,
  assessmentType
}) => {
  const { organizationId } = useParams<RouteParams>();
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('subscriber');
  const esgProductEnabled = useFeature('AionEsgProductVisible');
  const vcsProductEnabled = useFeature('AionVcsProductVisible');
  const refundFeatureEnabled = useFeature('EnableSubscriptionRefundButtonUI');
  const recurringBillingMessageFeatureEnabled = useFeature('RecurringBillingAutoRenewalStatusMessageEnabled');
  const [openUpgradeDialog, setOpenUpgradeDialog] = React.useState<boolean>(false);
  const [openRefundDialog, setOpenRefundDialog] = React.useState<boolean>(false);
  const { hasSubscriptionsPermission } = usePermission(ActivityAction.Write);
  const { hasSubscriptionsPermission: hasSubscriptionsManagePermission } = usePermission(ActivityAction.Manage);
  const { description, expirationDate: currentSubscriptionExpirationDate, isMain, type } = subscription;
  const isFoundation = mainSubscription.description === SubscriptionTiers.Foundation;
  const isUpgradeAvailable = isMain && (mainSubscription.isUpgradeAvailable || isFoundation);
  const isRenewalAvailable = isMain && mainSubscription.isRenewalAvailable && !isFoundation;
  const expirationDate =
    isMain && mainSubscription.expirationDate ? mainSubscription.expirationDate : currentSubscriptionExpirationDate;
  const isExpiring = moment(expirationDate).isBetween(moment(), moment().add(getRenewalAvailableInDays(), 'days'));
  const isExpired = moment(expirationDate).isBefore();
  const showRenewal = isRenewalAvailable && (isExpiring || isExpired);
  const Logo = LOGOS[type];
  const isChild = Boolean(organizationInfo?.isChild);
  const isParent = Boolean(organizationInfo?.isParent);
  const hideEsgCard = subscription.type === SubscriptionTypePublicId.ChasSocialSustainability && !esgProductEnabled;
  const hideVcsCard = subscription.type === SubscriptionTypePublicId.ChasVCS && !vcsProductEnabled;
  const showScopeChangeLink =
    (mainSubscription.description === 'CHAS Standard' || mainSubscription.description === 'CHAS Advanced') &&
    !showRenewal;
  const isPaymentPending = mainSubscription.paymentStatus === PaymentStatus.Pending;
  const isRecurringBillingMessageEnabled =
    recurringBillingMessageFeatureEnabled && mainSubscription.recurringBillingEnabled === true;

  const clearOperationStoredData = (operationType: string) => {
    operationSessionKeys.forEach(key => {
      sessionStorage.removeItem(`${key}${operationType}`.replace('organizationId', organizationId));
    });
  };

  const handleRefundClick = () => {
    if (organizationId && subscription.id) {
      dispatch(
        requestRefund({
          organizationId,
          subscriptionId: subscription.id,
          afterSuccess: () => {
            dispatch(refetchSubscriptions(organizationId));
          }
        })
      );
    }
    setOpenRefundDialog(false);
  };
  const handleUpgradeClick = () => {
    if (isParent && organizationInfo) {
      setOpenUpgradeDialog(true);
      const progressPayload = {
        organizationInfo,
        userInfo: null,
        mainSubscription,
        products: {
          productCodes: [],
          productIds: []
        },
        operationType: OperationType.Upgrade,
        stepName: 'UpgradeStart'
      };

      try {
        updateLead(progressPayload);
      } catch {}
      return;
    }

    clearOperationStoredData('Upgrade');
    history.push(`/${organizationId}/upgrade`);
  };

  if (hideEsgCard || hideVcsCard) {
    return null;
  }

  return (
    <Paper classes={{ root: classes.wrapper }}>
      <Box display="flex" alignItems="flex-start">
        {Logo ? (
          <Logo className={classes.logo} data-testid={TestLocators.logo} />
        ) : (
          <Box className={clsx(classes.noLogo, classes.logo)} />
        )}
        <Box marginLeft={2}>
          <Typography variant="h6" classes={{ root: classes.title }} data-testid={TestLocators.description}>
            {description}
          </Typography>
          {expirationDate && moment(expirationDate).isBefore(FOUNDATION_EXPIRATION_DATE) && (
            <>
              {isRecurringBillingMessageEnabled ? (
                <Typography
                  component="div"
                  classes={{ root: classes.text }}
                  data-testid={TestLocators.expirationDate}
                  style={{ margin: 0, lineHeight: 1.4 }}
                >
                  {t('membership.subscription.autoRenew', 'Membership is set to auto-renew')}{' '}
                  {localizeDate(
                    {
                      value: `${moment
                        .utc(expirationDate)
                        .local()
                        .format('YYYY-MM-DD')}T00:00:00`,
                      config: { day: '2-digit', month: '2-digit', year: 'numeric' }
                    },
                    t,
                    formats.dateCustom
                  )}
                </Typography>
              ) : isExpired ? (
                <Box display="flex">
                  <ReportProblemOutlinedIcon color="error" fontSize="small" />
                  <Typography
                    component="div"
                    classes={{ root: clsx(classes.textError, classes.text) }}
                    data-testid={TestLocators.expirationDate}
                  >
                    {t('membership.subscription.expired', 'Expired')}{' '}
                    {localizeDate(
                      {
                        value: `${moment
                          .utc(expirationDate)
                          .local()
                          .format('YYYY-MM-DD')}T00:00:00`,
                        config: { day: '2-digit', month: '2-digit', year: 'numeric' }
                      },
                      t,
                      formats.dateCustom
                    )}
                  </Typography>
                </Box>
              ) : (
                <Typography
                  component="div"
                  classes={{ root: classes.text }}
                  data-testid={TestLocators.expirationDate}
                  style={{ margin: 0, lineHeight: 1.4 }}
                >
                  {t('membership.subscription.expires', 'Expires')}{' '}
                  {localizeDate(
                    {
                      value: `${moment
                        .utc(expirationDate)
                        .local()
                        .format('YYYY-MM-DD')}T00:00:00`,
                      config: { day: '2-digit', month: '2-digit', year: 'numeric' }
                    },
                    t,
                    formats.dateCustom
                  )}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
      {isMain && (
        <Box display="flex" mt={1.5}>
          <Typography classes={{ root: classes.text }}>{assessmentType}</Typography>
          {showScopeChangeLink && (
            <NavLink className={classes.scopeChangeLink} to={`/${organizationId}/scope-change`}>
              {t('membership.subscription.scopeChange', ' (Change scope)')}
            </NavLink>
          )}
        </Box>
      )}
      {hasSubscriptionsPermission ? (
        <>
          <Box display="flex" height={36}>
            {isUpgradeAvailable && !isChild && (
              <Button
                color="primary"
                variant="contained"
                classes={{ root: classes.button }}
                data-testid={TestLocators.upgradeBtn}
                onClick={handleUpgradeClick}
                disabled={isPaymentPending}
              >
                {t('membership.subscription.upgrade', 'Upgrade')}
              </Button>
            )}
            {showRenewal && !(isChild || isParent) && (
              <Button
                color="primary"
                variant="outlined"
                component={Link}
                to={`/${organizationId}/renew`}
                classes={{ root: classes.button }}
                data-testid={TestLocators.renewBtn}
                onClick={() => {
                  clearOperationStoredData('Renewal');
                }}
              >
                {t('membership.subscription.renew', 'Renew')}
              </Button>
            )}
            {hasSubscriptionsManagePermission && refundFeatureEnabled && (
              <Button
                color="primary"
                variant={showRenewal ? 'contained' : 'outlined'}
                data-testid={TestLocators.refundBtn}
                onClick={() => setOpenRefundDialog(true)}
                disabled={isPaymentPending}
              >
                {t('membership.subscription.refund', 'Refund')}
              </Button>
            )}
          </Box>
          {openUpgradeDialog && (
            <InfoDialog
              open={openUpgradeDialog}
              strings={{
                title: t('subscriber:upgrade.dialogTitle', 'Upgrade Membership'),
                confirm: t('subscriber:common.close', 'Close'),
                content: (
                  <Trans key="subscriber:upgrade.dialogContent">
                    Show your commitment to creating a safer, risk-free and more compliant world for all of your
                    employees by upgrading your membership. <br />
                    <br />
                    To upgrade your membership, please contact a member of our award winning team on{' '}
                    <a href="tel:03455219222" className={classes.navLink}>
                      345 521 9222
                    </a>{' '}
                    who will be happy to help.
                  </Trans>
                )
              }}
              onConfirm={() => setOpenUpgradeDialog(false)}
            />
          )}
          {openRefundDialog && (
            <InfoDialog
              open={openRefundDialog}
              strings={{
                title: t('subscriber:refund.dialogTitle', 'Are you sure?'),
                content: t(
                  'subscriber:refund.dialogContent',
                  "This action will revert Contractor state to what it was before the purchase. If Contractor had no similar membership before, he'll loose access to all related Questionnaire modules"
                ),
                confirm: t('subscriber:common.confirm', 'Confirm'),
                cancel: t('subscriber:common.discard', 'Discard')
              }}
              onConfirm={handleRefundClick}
              onCancel={() => setOpenRefundDialog(false)}
            />
          )}
        </>
      ) : (
        <Box height={36} />
      )}
    </Paper>
  );
};
